<template>
  <div>
    <div class="dialog--container" :class="containerClasses" ref="dialog" role="dialog" :aria-label="title"
         @transitionend="handleContainerTransitionEnd" tabindex="0" @click.stop>
      <h3 v-if="title.length" ref="title">{{ title }}</h3>
      <div class="dialog--body" ref="body" :style="{'max-height': bodyMaxHeight}">
        <slot name="body"></slot>
      </div>
      <div class="layout--actions dialog--actions" ref="actions">
        <a v-if="!isAlert" class="dialog--deny" href="#"
           @click.prevent="$emit('cancel')">{{ labelDeny }}</a>
        <a class="dialog--accept" href="#"
           @click.prevent="$emit('submit', getDialogFormData())">{{ labelConfirm }}</a>
      </div>
    </div>
    <div class="dialog--overlay" :class="overlayClasses" @click.stop="$emit('close')"></div>
  </div>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2023-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 * @version 1.1.0
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @changelog
 *		2024-05-14 (1.0.0)
 *			- Add role="dialog" to <peppermill-dialog> container.
 *			- Give linkEle prop a type in <peppermill-dialog> container.
 *			- Give lastActiveElement data a type in <peppermill-dialog> container.
 *			- Remove "outline=none" CSS property values in <peppermill-dialog> container.
 *			- Add "cancel" emits in <peppermill-dialog> container.
 *
 *		2024-06-17 (1.1.0) (jhoward)
 *			- Add support for Pepvalidate V3 forms to <peppermill-dialog>.
 */

import {ComponentPublicInstance, defineComponent, PropType} from "vue";

export interface PeppermillDialogComponent extends ComponentPublicInstance {
  getDialogFormData: () => FormData | null;
}
export default defineComponent({
  name: "peppermill-dialog",
  emits: {
    close: () => true,
    cancel: () => true,
    submit: (formData: FormData | null) => true,
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    labelConfirm: {
      type: String,
      default: 'OK'
    },
    labelDeny: {
      type: String,
      default: 'CANCEL'
    },
    isAlert: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    linkEle: {
      type: HTMLElement as PropType<HTMLElement | null>,
      default: () => null
    },
    wide: {
      type: Boolean,
      default: false
    },
    hideOverlay: {
      type: Boolean,
      default: false
    }
  },
  mounted: function () {
    $(() => {
      if (window.pepMaterial && window.pepMaterial.globalInitialize) {
        window.pepMaterial.globalInitialize.call(this.$el);
      } else if (window.pepvalidateInitForm && this.$el.querySelector('form.pepvalidate-v3')) {
        window.pepvalidateInitForm.call(this.$el.querySelector('form.pepvalidate-v3') as HTMLFormElement);
      }
    });

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
  },
  data: function () {
    return {
      // lastActiveElement: null as HTMLElement | null,
      opening: false,
      closing: false,
      windowHeight: window.innerHeight,
      // windowScrollY: 0,
    };
  },
  computed: {
    bodyMaxHeight() {
      this.active;
      let maxHeight = this.windowHeight;
      if (this.$refs.dialog) {
        maxHeight -= parseInt(getComputedStyle(this.$refs.dialog as HTMLDivElement).paddingTop, 10);
      }
      if (this.$refs.title) {
        maxHeight -= (this.$refs.title as HTMLHeadingElement).clientHeight;
      }

      if (this.$refs.actions) {
        maxHeight -= (this.$refs.actions as HTMLDivElement).clientHeight;
      }
      return maxHeight + 'px';
    },
    containerClasses() {
      return {
        'dialog--container-active': this.active,
        'dialog--container-opening': this.opening,
        'dialog--container-closing': this.closing,
        'dialog--container-wide': this.wide,
      };
    },
    overlayClasses() {
      return {
        'dialog--overlay-show': !this.hideOverlay && this.active
      };
    },
  },
  watch: {
    active: function () {
      if (this.active) {
        //opening
        // this.lastActiveElement = document.activeElement as HTMLElement | null;

        setTimeout(() => {
          (this.$refs.dialog as HTMLDivElement).focus();
        }, 512);

      } else if (this.linkEle) {
        //closing
        if (!this.closing && !this.opening) {
          //re-focus last focused element before opening dialog
            this.linkEle.focus();
        }
      }
    }
  },
  methods: {
    handleContainerTransitionEnd: function (event: TransitionEvent) {
      if ('' === event.pseudoElement && 'opacity' === event.propertyName) {
        if (this.$el.classList.contains('dialog--container-opening')) {
          this.$el.classList.remove('dialog--container-opening');
        } else if (this.$el.classList.contains('dialog--container-closing')) {
          this.$el.classList.remove('dialog--container-closing');
        }
      }
    },

    getDialogFormData(): FormData | null {
      const formNode = this.$el.querySelector('form');
      if (formNode) {
        return new FormData(formNode);
      }

      return null;
    }
  }
})
</script>

<style scoped>
.dialog--overlay {
  position: fixed;
  z-index: 35;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.75);
  visibility: hidden;
  opacity: 0;

  -webkit-transition: opacity 250ms ease-in, visibility 1ms linear 250ms, background-color 250ms ease-in 250ms;
  transition: opacity 250ms ease-in, visibility 1ms linear 250ms, background-color 250ms ease-in 250ms;
}

.dialog--overlay-show {
  visibility: visible;
  opacity: 1;

  -webkit-transition: opacity 250ms ease-out, visibility 1ms linear 1ms, background-color 250ms ease-out 1ms;
  transition: opacity 250ms ease-out, visibility 1ms linear 1ms, background-color 250ms ease-out 1ms;
}

.dialog--overlay-clear {
  background-color: rgba(0, 0, 0, 0);

  -webkit-transition: opacity 250ms ease-out, visibility 1ms linear 1ms, background-color 0ms linear 1ms;
  transition: opacity 250ms ease-out, visibility 1ms linear 1ms, background-color 0ms linear 1ms;
}

.dialog--container {
  position: fixed;
  top: 50%;
  left: 50%;
  padding: 24px 0 0 0;
  opacity: 0;
  visibility: hidden;
  border-radius: 2px;
  background-color: #FFF;
  box-shadow: 0 0 32px rgba(0, 0, 0, 0.54);
  color: #000;
  color: rgba(0, 0, 0, 0.87);
  width: 100%;
  box-sizing: border-box;

  max-width: 400px;
  max-height: 100vh;
  overflow: auto;
  font-size: 1.2em;
  will-change: transform, opacity, visibility;
  z-index: 50;

  transform: translateY(10px) translateY(-50%) translateX(-50%);
  transition: transform 250ms ease-in, opacity 250ms ease-in, visibility 1ms linear 250ms;
}

.dialog--container-wide {
  max-width: 800px;
}

.dialog--container-active {
  visibility: visible;
  opacity: 1;

  transform: translateY(-50%) translateX(-50%);
  transition: transform 250ms ease-out, opacity 250ms ease-out, visibility 1ms linear 1ms;
}

.dialog--container h3 {
  color: #000;
  color: rgba(0, 0, 0, 0.54);
  margin: 0;
  padding: 0 24px 20px 24px;
}

.dialog--body {
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.dialog--body:deep(p),
.dialog--body:deep(table) {
  margin: 0;
  padding: 0 24px 24px 24px;
}

.dialog--body:deep(table) {
  width: 100%;
}

.dialog--body:deep(label) {
  width: calc(100% - 48px);
  margin: 0 24px 24px 24px;
}

.dialog--body:deep(p.highlight) {
  position: relative;
  background-color: #FFFF8D;
}

.dialog--body:deep(p.highlight:before) {
  content: '';
  position: absolute;
  top: -1em;
  right: 0;
  left: 0;
  height: 1em;
  background-color: #FFFF8D;
}

.dialog--container:deep(svg),
.dialog--container:deep(.icon) {
  width: 24px;
  height: 24px;
  margin: 8px 8px 0 0;
}

.dialog--container:deep(.btn svg),
.dialog--container:deep(.btn .icon) {
  background-color: #dfebe9;
  border: 0.125em solid #326f5f;
  border-radius: 50%;
  margin: -0.25em 0 0 0;
  padding: 0.125em;
  vertical-align: middle;

  transform: translateX(-0.75em) scale(1.5);
}

.dialog--container:deep(.btn.btn-highlight svg),
.dialog--container:deep(.btn.btn-highlight .icon) {
  color: #000;
}

.dialog--container:deep(svg.icon-large),
.dialog--container:deep(.icon.icon-large) {
  width: 100%;
  height: 48px;
  margin: 0;
}

.dialog--container:deep(hr) {
  margin: 0 0 1em 0;
}


/*ACTIONS*/
.layout--actions {
  padding: 8px 8px 8px 24px;
}

.layout--actions::after {
  content: '';
  display: table;
  clear: both;
}

.layout--actions a {
  height: 36px;
  margin-left: 8px;
  padding: 0 8px;
  line-height: 36px;
  vertical-align: middle;
  text-decoration: none;
  color: rgb(var(--primary-color, 0, 121, 102));
  text-transform: uppercase;
  float: right;
  background-color: #FFF;
  min-width: 64px;
  max-width: 50%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  will-change: background-color;

  transition: background-color 250ms ease-in;
}

.layout--actions a.layout--action_button-disabled {
  background-color: #EEE !important;
  color: #808080 !important;
  cursor: default;
}

.layout--actions a:hover,
.layout--actions a:focus {
  background-color: #EEE;

  transition: background-color 250ms ease-out;
}

.layout--actions a:active {
  background-color: #CCC;

  transition: background-color 250ms ease-out;
}
</style>